import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Marquee from "@/components/Marquee";
import Postcard from "@/components/Postcard";
import ItineraryCard from "@/components/ItineraryCard";
import { api } from "@/lib/api";
import { SOFT_LAUNCH } from "@/config/softLaunch";

export default function Landing() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/itineraries/featured").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  // Temporary GTM soft-launch (see src/config/softLaunch.js) — footer links from other
  // pages navigate here with a #featured-section hash; scroll to it once mounted.
  useEffect(() => {
    if (SOFT_LAUNCH && window.location.hash === "#featured-section") {
      setTimeout(() => {
        document.getElementById("featured-section")?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, []);

  const heroPc = featured[0]
    ? {
        region: `${featured[0].destination_country} · ${featured[0].duration_days} Days`,
        title: featured[0].title,
        seller_name: featured[0].seller?.name?.toUpperCase() || "NIRNO CREATOR",
        price: featured[0].price_usd,
        cover_emoji: featured[0].cover_emoji,
        cover_gradient: featured[0].cover_gradient,
        vol: 47,
      }
    : null;

  // Temporary GTM soft-launch (see src/config/softLaunch.js) — scrolls to Featured
  // Routes instead of navigating to /explore. Revert by flipping SOFT_LAUNCH to false.
  const scrollToFeatured = (e) => {
    e.preventDefault();
    document.getElementById("featured-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="px-6 sm:px-10 pt-12 sm:pt-20 pb-16 max-w-[1280px] mx-auto grid gap-12 lg:grid-cols-[1.3fr_1fr] items-center">
        <div className="fade-up">
          <span className="eyebrow">◆ Vol. I — 2026</span>
          <h1 className="h-display mt-5 text-[44px] sm:text-[64px] lg:text-[80px]">
            Real trips,<br />
            by real <em>travellers.</em><br />
            <span className="outline">Posted to you.</span>
          </h1>
          <p className="mt-6 max-w-[520px] text-[16px] sm:text-[17px] leading-[1.6] text-[var(--ink2)]">
            A marketplace for hyper-specific travel itineraries. Every stop, every cost, every honest tip — already figured out by someone who's been there.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {SOFT_LAUNCH ? (
              <button onClick={scrollToFeatured} data-testid="hero-cta-browse" className="btn btn-solid btn-lg">
                Browse all routes →
              </button>
            ) : (
              <Link to="/explore" data-testid="hero-cta-browse" className="btn btn-solid btn-lg">
                Browse all routes →
              </Link>
            )}
            <Link to="/auth?mode=signup" data-testid="hero-cta-join" className="btn btn-ghost btn-lg">
              Join for free
            </Link>
            <span className="ml-2 mono text-[11px] uppercase tracking-[0.15em] text-[var(--ink3)]">
              ★ 18,400 travellers
            </span>
          </div>
        </div>
        <div className="fade-up delay-2 max-w-[420px] mx-auto w-full">
          {heroPc && <Postcard data={heroPc} />}
        </div>
      </section>

      <Marquee />

      {/* HOW IT WORKS */}
      <section className="px-6 sm:px-10 py-20 max-w-[1280px] mx-auto" data-testid="how-it-works">
        <span className="eyebrow">◆ How Nirno works</span>
        <h2 className="h-display mt-3 text-[36px] sm:text-[52px] max-w-[820px]">
          Three steps from <em>idea</em> to itinerary in your pocket.
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            {
              n: "01",
              title: "Browse routes by people you trust",
              body: "Every creator is invite-only — personally vetted before they can publish. Read Day 1 of any itinerary, free, no login required.",
            },
            {
              n: "02",
              title: "Pay once, keep forever",
              body: "₹99 to ₹5,000 per route. No subscription, no expiry. Creators keep the full sale price — no commission taken.",
            },
            {
              n: "03",
              title: "Travel offline, day by day",
              body: "Open your library on any device. Daily cards, costs, maps, creator tips. Export to PDF for offline use when you're on the road.",
            },
          ].map((s, i) => (
            <div
              key={s.n}
              className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] p-7 hover:border-[var(--ink)] hover:-translate-y-1 transition-all"
              data-testid={`how-step-${i + 1}`}
            >
              <span className="eyebrow">Step {s.n}</span>
              <div className="serif text-[60px] mt-3 leading-none text-[var(--red)]">{s.n}</div>
              <div className="mt-4 text-[18px] font-bold leading-tight tracking-[-0.01em]">{s.title}</div>
              <p className="mt-3 text-[14px] leading-[1.6] text-[var(--ink2)]">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section id="featured-section" className="px-6 sm:px-10 py-12 max-w-[1280px] mx-auto" data-testid="featured-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="eyebrow">◆ Featured routes</span>
            <h2 className="h-display mt-2 text-[36px] sm:text-[44px]">
              Travellers who write down <em>everything.</em>
            </h2>
          </div>
          <Link to="/explore" className="btn btn-ghost btn-sm">See all routes →</Link>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {featured.slice(0, 6).map((it) => (
            <ItineraryCard key={it.itinerary_id} itin={it} disableLink={SOFT_LAUNCH} />
          ))}
        </div>
      </section>

      {/* CREATOR CTA */}
      <section className="px-6 sm:px-10 pb-20 max-w-[1280px] mx-auto" data-testid="creator-cta">
        <div className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[24px] px-8 py-14 sm:px-16 sm:py-20 text-center">
          <span className="eyebrow">◆ Are you a travel creator?</span>
          <h2 className="h-display mt-4 text-[36px] sm:text-[44px]">
            Monetise your <em>best</em> work.
          </h2>
          <p className="mt-4 max-w-[480px] mx-auto text-[15px] leading-[1.65] text-[var(--ink2)]">
            Your travel expertise lives in Instagram carousels and YouTube descriptions that earn you nothing. Package it once. Get paid every time someone goes where you've been.
          </p>
          <a
            href="mailto:hello@nirno.co.in?subject=Interested in selling on Nirno"
            data-testid="creator-cta-button"
            className="btn btn-ghost mt-7 inline-flex"
          >
            Reach out to apply →
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
